/**
 * TollPaidModal.tsx — R5 #11
 *
 * Monopoly-style forced-acknowledge modal shown when the lander pays toll on
 * an opponent's facility tile. Toll has already been deducted before this
 * renders; modal is informational + acknowledgement only.
 *
 * Spec constraints:
 *   - NO backdrop close (click-outside does nothing)
 *   - NO Esc close
 *   - Single "Acknowledge" button
 *   - Native <dialog open> per spec
 *
 * VISIBLE WHEN: pendingTollPayment !== null
 * TEST PATH: land on opponent's built tile → modal renders before turn continues
 */

import type React from "react";
import { useEffect, useRef } from "react";
import { FACILITIES } from "../../store/facilities.js";
import { useGameStore } from "../../store/game-store.js";
import styles from "./TollPaidModal.module.css";

const SEAT_LABELS = ["P1", "P2", "P3", "P4"] as const;

export function TollPaidModal(): React.ReactElement | null {
  const pendingTollPayment = useGameStore((s) => s.pendingTollPayment);
  const dismissTollPayment = useGameStore((s) => s.dismissTollPayment);
  // R11.2 — gate behind board-walk so toll modal doesn't pop before arrival.
  const boardAnimationActive = useGameStore((s) => s.boardAnimationActive);
  const dialogRef = useRef<HTMLDialogElement>(null);

  // Spec: NO Esc close — intercept cancel event on dialog element.
  useEffect(() => {
    const el = dialogRef.current;
    if (el === null) return;
    const blockEsc = (e: Event) => {
      e.preventDefault();
    };
    el.addEventListener("cancel", blockEsc);
    return () => el.removeEventListener("cancel", blockEsc);
  }, []);

  if (pendingTollPayment === null) return null;
  if (boardAnimationActive) return null;

  const { tileIdx, facilityId, ownerSeat, amount, newLanderBalance } = pendingTollPayment;
  const newOwnerBalance = pendingTollPayment.newOwnerBalance ?? null;

  const facilityDef = FACILITIES.find((f) => f.id === facilityId);
  const facilityName = facilityDef?.name ?? facilityId;
  const iconSrc = facilityDef?.iconSrc ?? `/images/facilities/facility_${facilityId}.png`;
  const ownerLabel = SEAT_LABELS[ownerSeat] ?? `P${ownerSeat + 1}`;

  return (
    // Spec: NO backdrop close — outer div ignores clicks
    <div className={styles.scrim} data-testid="toll-paid-scrim">
      <dialog
        ref={dialogRef}
        open
        className={styles.dialog}
        data-testid="toll-paid-dialog"
        aria-labelledby="toll-paid-title"
        aria-modal="true"
      >
        <div className={styles.panel}>
          <div className={styles.title} id="toll-paid-title" data-testid="toll-paid-title">
            Toll Paid
          </div>

          <div className={styles.facilityRow}>
            <img
              src={iconSrc}
              alt={facilityName}
              className={styles.facilityIcon}
              onError={(e) => {
                (e.currentTarget as HTMLImageElement).style.display = "none";
              }}
            />
            <span className={styles.facilityName} data-testid="toll-facility-name">
              {facilityName}
            </span>
          </div>

          <div className={styles.narrative} data-testid="toll-narrative">
            You stepped on {ownerLabel}&apos;s {facilityName} (T
            {tileIdx.toString().padStart(2, "0")}). Paid {amount} token{amount !== 1 ? "s" : ""}.
          </div>

          <div className={styles.amountLine} data-testid="toll-amount">
            −{amount} 💰 → {ownerLabel}
          </div>

          {newOwnerBalance !== null && (
            <div className={styles.amountLine} data-testid="toll-owner-delta">
              {ownerLabel} receives: +{amount} token{amount !== 1 ? "s" : ""} (balance:{" "}
              {newOwnerBalance})
            </div>
          )}

          <div className={styles.balanceLine} data-testid="toll-new-balance">
            Your balance: {newLanderBalance} tokens
          </div>

          <button
            type="button"
            className={styles.ackBtn}
            data-testid="btn-toll-acknowledge"
            onClick={dismissTollPayment}
          >
            ✓ Acknowledge
          </button>
        </div>
      </dialog>
    </div>
  );
}
